<template>
  <div ref="stage" class="est-room__stage est-room__stage--live"
       :class="{ 'is-fullscreen': isFullscreen, 'is-split': isSplit }"
       :style="stageStyle">
    <!-- ⚠ `remotePlayer` / `localPlayer` id'leri sözleşmedir: akış geldiğinde
         store'daki `initializeSimplepeer` bu id'leri `getElementById` ile bulup
         `srcObject` bağlıyor. -->
    <video ref="remotePlayer" id="remotePlayer" class="est-room__video"
           controlsList="" autoplay playsinline></video>

    <!-- Akış kesildiğinde `<video>` son kareyi tutar; karşı taraf çoktan gitmişken
         ekranda donmuş bir görüntü kalıyordu. Kare sahne rengiyle örtülür. -->
    <div v-if="!remoteMediaLive" class="est-room__placeholder est-room__placeholder--over">
      <span class="est-room__placeholder-icon" aria-hidden="true"><font-awesome-icon icon="fa-solid fa-video-slash" /></span>
      <p class="est-room__placeholder-title">{{ $t('OnlineMeetingComponent.RemoteVideoUnavailable') }}</p>
    </div>

    <div class="est-room__pip">
      <!-- Kendi görüntün aynalanır (aynaya bakma alışkanlığı), karşı tarafınki
           asla: onun yazısı ve hareketi ters görünürdü. -->
      <video ref="localPlayer" id="localPlayer" class="est-room__video est-room__video--mirrored est-room__video--cover"
             muted autoplay playsinline></video>
      <div v-if="!cameraOn" class="est-room__pipoff" aria-hidden="true">
        <span v-if="selfInitials" class="est-room__avatar est-room__avatar--pip">{{ selfInitials }}</span>
        <font-awesome-icon v-else icon="fa-solid fa-video-slash" />
      </div>
    </div>

    <meeting-controls-component :camera-on="cameraOn"
                                :mic-on="micOn"
                                :disabled="disabled"
                                show-fullscreen
                                :is-fullscreen="isFullscreen"
                                show-layout
                                :is-split="isSplit"
                                show-end
                                :is-ending="isEnding"
                                @toggle-layout="isSplit = !isSplit"
                                @toggle-camera="$emit('toggle-camera')"
                                @toggle-mic="$emit('toggle-mic')"
                                @toggle-fullscreen="toggleFullscreen"
                                @end-meeting="$emit('end-meeting')" />
  </div>
</template>

<script>
  import MeetingControlsComponent from './MeetingControlsComponent.vue'
  import { nameInitials } from '../../../helpers/nameInitials'

  // Görüşme hâlindeki sahne: uzak/yerel oynatıcı ve kontrol şeridi tek yerde.
  //
  // Sahnenin iki düzeni vardır, kartta da tam ekranda da: varsayılan FaceTime /
  // Meet web kalıbı — karşı taraf sahneyi kırpılmadan kaplar, kendi görüntün köşede
  // küçük pencerede; düğmeyle `is-split` — sahne ortadan ikiye bölünür, iki görüntü
  // de karoya kırpılmadan sığar. Karolar her yerde alt alta (yan yana yok).
  export default {
    name: 'MeetingStageComponent',
    components: { MeetingControlsComponent },
    props: {
      cameraOn: Boolean,
      micOn: Boolean,
      disabled: Boolean,
      isEnding: Boolean,
      selfName: {
        type: String,
        default: ''
      }
    },
    data() {
      return {
        isFullscreen: false,
        isSplit: false,
        // Karşı tarafın görüntü oranı (videoWidth / videoHeight). Dar ekranda tekli
        // görünümde sahne bu oranı alır (CSS `--est-remote-aspect`): telefon
        // kamerası dikey, bilgisayar kamerası yatay. Akış gelmeden null (CSS 3/4).
        remoteAspect: null,
        // Kendi görüntünün oranı: köşe penceresi (`--est-local-aspect`) bu orana
        // uyar — telefon kamerası dikeyken 16/9 pencere yüzü yatay kırpıyordu.
        localAspect: null
      }
    },
    computed: {
      selfInitials() {
        return nameInitials(this.selfName);
      },
      stageStyle() {
        const style = {};
        if (this.remoteAspect) style['--est-remote-aspect'] = String(this.remoteAspect);
        if (this.localAspect) style['--est-local-aspect'] = String(this.localAspect);
        return style;
      },
      // Peer'ın bağlı olması karşı tarafın görüntüsünün aktığı anlamına gelmez;
      // bayrak store'da uzak track'lerin `muted`/`ended` durumundan üretilir.
      remoteMediaLive() {
        return this.$store.state.Meeting.remoteMediaLive;
      }
    },
    methods: {
      // `loadedmetadata` ilk boyutu, `resize` sonraki değişimleri (karşı taraf
      // telefonu çevirdi, kamera değişti) verir.
      updateRemoteAspect() {
        const video = this.$refs.remotePlayer;
        if (!video || !video.videoWidth || !video.videoHeight) return;

        this.remoteAspect = video.videoWidth / video.videoHeight;
      },
      updateLocalAspect() {
        const video = this.$refs.localPlayer;
        if (!video || !video.videoWidth || !video.videoHeight) return;

        this.localAspect = video.videoWidth / video.videoHeight;
      },
      toggleFullscreen() {
        if (this.isFullscreen) {
          this.exitFullscreen();
        } else {
          this.enterFullscreen();
        }
      },
      enterFullscreen() {
        const el = this.$refs.stage;
        if (!el) return;

        // Tam ekran isteği videoya değil sahneye verilir; videoya verilseydi
        // kontrol şeridi ve küçük pencere tam ekranda kaybolurdu.
        //
        // iPhone'da `<video>` dışı elemanlar için Fullscreen API yoktur; istek
        // ya hiç bulunmaz ya da reddedilir. Her iki durumda `is-fullscreen`
        // sınıfı devrede kalır ve sahne CSS ile tüm ekranı kaplar.
        const request = el.requestFullscreen || el.webkitRequestFullscreen;

        if (request) {
          const result = request.call(el);
          if (result && typeof result.catch === 'function') {
            result.catch(() => {});
          }
        }

        this.isFullscreen = true;
      },
      exitFullscreen() {
        const exit = document.exitFullscreen || document.webkitExitFullscreen;

        if (exit && (document.fullscreenElement || document.webkitFullscreenElement)) {
          const result = exit.call(document);
          if (result && typeof result.catch === 'function') {
            result.catch(() => {});
          }
        }

        this.isFullscreen = false;
      },
      // Esc ile ya da sistem tarafından tam ekrandan çıkıldığında düğme ikonu ve
      // düzen geride kalmasın.
      onFullscreenChange() {
        if (!(document.fullscreenElement || document.webkitFullscreenElement)) {
          this.isFullscreen = false;
        }
      }
    },
    mounted() {
      document.addEventListener('fullscreenchange', this.onFullscreenChange);
      document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);

      const remote = this.$refs.remotePlayer;
      if (remote) {
        remote.addEventListener('loadedmetadata', this.updateRemoteAspect);
        remote.addEventListener('resize', this.updateRemoteAspect);
        this.updateRemoteAspect();
      }

      const local = this.$refs.localPlayer;
      if (local) {
        local.addEventListener('loadedmetadata', this.updateLocalAspect);
        local.addEventListener('resize', this.updateLocalAspect);
        this.updateLocalAspect();
      }
    },
    beforeDestroy() {
      document.removeEventListener('fullscreenchange', this.onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);

      const remote = this.$refs.remotePlayer;
      if (remote) {
        remote.removeEventListener('loadedmetadata', this.updateRemoteAspect);
        remote.removeEventListener('resize', this.updateRemoteAspect);
      }

      const local = this.$refs.localPlayer;
      if (local) {
        local.removeEventListener('loadedmetadata', this.updateLocalAspect);
        local.removeEventListener('resize', this.updateLocalAspect);
      }

      // Bileşen tam ekrandayken yok edilirse tarayıcı tam ekranda kalırdı.
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        this.exitFullscreen();
      }
    }
  }
</script>
